import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ListOrdered, Network, Plus, Vote, X } from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { cn } from '../../../../lib/utils';
import { MAX_BRACKET_PLAYERS } from '../../../../lib/bracket';
import {
  hasVoteCategory,
  isVoteCategory,
  newCategoryId,
  newStage,
  newVoteCategory,
  stageActions,
  stageConfig,
  validateStage,
} from '../../../../lib/stages';
import { addStage, updateStage } from '../../../../database/tournamentStages';
import { handleFirebaseError } from '../../../../utils/errorHandler';
import BracketView from '../../../Public/stages/BracketView';
import LeaderboardStageView from '../../../Public/stages/LeaderboardStageView';

const PLACEHOLDER_PLAYERS_BY_ID = {
  'seed:1': { name: 'Player 1' },
  'seed:2': { name: 'Player 2' },
  'seed:3': { name: 'Player 3' },
};

// Copied from TournamentSettingsForm.js by design — not imported, so the two
// forms can diverge without coupling this dialog to the tournament settings form.
const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const Segmented = ({ value, onChange, options, disabled }) => (
  <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        role="radio"
        aria-checked={value === opt.value}
        disabled={disabled}
        onClick={() => onChange(opt.value)}
        className={cn(
          'border px-3 py-2 text-left transition-colors',
          value === opt.value
            ? 'border-heat bg-heat/10 text-foreground'
            : 'border-border/60 hover:border-border',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        <div className="text-sm font-medium">{opt.label}</div>
        {opt.hint && <div className="text-xs text-muted-foreground">{opt.hint}</div>}
      </button>
    ))}
  </div>
);

// Shrinks its content (never grows it) so it always fits the box, keeping the
// dialog free of scrollbars however big the bracket gets.
const FitToBox = ({ children }) => {
  const boxRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    if (!box || !content) return undefined;
    const fit = () => {
      const w = content.scrollWidth;
      const h = content.scrollHeight;
      if (!w || !h) return;
      setScale(Math.min(1, box.clientWidth / w, box.clientHeight / h));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={boxRef} className="relative min-h-0 flex-1 overflow-hidden">
      <div
        ref={contentRef}
        className="absolute left-0 top-0 w-max origin-top-left"
        style={{ transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
};

const TYPE_OPTIONS = [
  {
    value: 'leaderboard',
    icon: ListOrdered,
    label: 'Leaderboard',
    hint: 'Give players points. Most points wins.',
  },
  { value: 'bracket', icon: Network, label: 'Bracket', hint: 'Single or double elimination.' },
];

const StageEditorDialog = ({ open, onOpenChange, tournamentId, stages, stage, rosterCount }) => {
  const index = stage ? stages.findIndex((s) => s.id === stage.id) : stages.length;
  const prev = stages[index - 1];
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(
      stage
        ? { ...stage }
        : newStage({
            name: `Stage ${stages.length + 1}`,
            type: 'bracket',
            advanceCount: prev ? Math.min(8, prev.advanceCount || 8) : null,
          })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage?.id]);

  const locked = !!stage && stage.status !== 'pending';
  const advanceEditable = stage ? stageActions(stages, index).canEditAdvance : !!prev;
  const voteTaken = stages.some((s) => s.id !== stage?.id && hasVoteCategory(s));

  const setField = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));

  const updateCategory = (id, name) =>
    setDraft((d) => ({
      ...d,
      categories: d.categories.map((c) => (c.id === id ? { ...c, name } : c)),
    }));

  const removeCategory = (id) =>
    setDraft((d) => ({ ...d, categories: d.categories.filter((c) => c.id !== id) }));

  const addCategory = () =>
    setDraft((d) => ({
      ...d,
      categories: [
        ...d.categories,
        { id: newCategoryId(), name: `Category ${d.categories.length + 1}` },
      ],
    }));

  const addVoteCategory = () =>
    setDraft((d) => ({ ...d, categories: [...d.categories, newVoteCategory()] }));

  const handleSave = async () => {
    setBusy(true);
    try {
      const config = stageConfig(draft);
      if (!stage) {
        await addStage(tournamentId, { ...newStage(), ...config, order: stages.length });
      } else if (locked) {
        await updateStage(tournamentId, stage.id, { name: config.name });
      } else if (advanceEditable) {
        await updateStage(tournamentId, stage.id, config);
      } else {
        await updateStage(tournamentId, stage.id, { ...config, advanceCount: stage.advanceCount });
      }
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not save the stage.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  const errors = draft ? validateStage(draft, prev) : [];
  const placeholderCount = prev ? draft?.advanceCount || 2 : rosterCount >= 2 ? rosterCount : 8;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {draft && (
        <DialogContent className="!max-w-6xl lg:h-[min(44rem,90vh)] lg:grid-rows-[auto_minmax(0,1fr)_auto] lg:overflow-hidden">
          <DialogHeader>
            <DialogTitle>{stage ? `Edit ${stage.name}` : 'Add stage'}</DialogTitle>
          </DialogHeader>

          <div className="grid min-h-0 gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
            <div className="grid min-w-0 content-start gap-5 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
              <Field label="Name" required>
                <Input
                  value={draft.name}
                  maxLength={60}
                  onChange={(e) => setField('name')(e.target.value)}
                />
              </Field>

              <Field label="Type">
                <div role="radiogroup" className="grid gap-3 sm:grid-cols-2">
                  {TYPE_OPTIONS.map(({ value, icon: Icon, label, hint }) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={draft.type === value}
                      disabled={locked}
                      onClick={() => setField('type')(value)}
                      className={cn(
                        'flex items-start gap-3 border p-3 text-left transition-colors',
                        draft.type === value
                          ? 'border-heat bg-heat/10'
                          : 'border-border/60 hover:border-border',
                        locked && 'pointer-events-none opacity-50'
                      )}
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <div className="text-sm font-medium">{label}</div>
                        <div className="text-xs text-muted-foreground">{hint}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </Field>

              {prev && (
                <Field
                  label={`Players advancing from ${prev.name}`}
                  hint={`The top N of ${prev.name} start this stage. Ties at the cut are settled when you complete ${prev.name}.`}
                >
                  <Input
                    type="number"
                    min={1}
                    max={draft.type === 'bracket' ? MAX_BRACKET_PLAYERS : 1000}
                    disabled={!advanceEditable}
                    value={draft.advanceCount ?? ''}
                    onChange={(e) => setField('advanceCount')(Number(e.target.value))}
                  />
                </Field>
              )}

              {draft.type === 'leaderboard' && (
                <>
                  <Field
                    label="Point categories"
                    hint="One column per map or category. A player's points add up across them."
                  >
                    <div className="grid gap-2">
                      {draft.categories.map((c) => (
                        <div key={c.id} className="flex items-center gap-2">
                          <Input
                            value={c.name}
                            maxLength={40}
                            disabled={locked}
                            onChange={(e) => updateCategory(c.id, e.target.value)}
                          />
                          {isVoteCategory(c) && <Badge variant="outline">Community vote</Badge>}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={locked || draft.categories.length <= 1}
                            onClick={() => removeCategory(c.id)}
                            aria-label={`Remove ${c.name || 'category'}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {draft.categories.some(isVoteCategory) && (
                        <p className="text-xs text-muted-foreground">
                          Average player rating, 1 to 10.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={locked || draft.categories.length >= 20}
                          onClick={addCategory}
                        >
                          <Plus className="h-4 w-4" />
                          Add category
                        </Button>
                        {!draft.categories.some(isVoteCategory) && !voteTaken && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={locked}
                            onClick={addVoteCategory}
                          >
                            <Vote className="h-4 w-4" />
                            Add community vote
                          </Button>
                        )}
                      </div>
                    </div>
                  </Field>
                </>
              )}

              {draft.type === 'bracket' && (
                <>
                  <Field label="Elimination">
                    <Segmented
                      value={draft.elimination}
                      onChange={setField('elimination')}
                      disabled={locked}
                      options={[
                        { value: 'single', label: 'Single' },
                        { value: 'double', label: 'Double' },
                      ]}
                    />
                  </Field>
                </>
              )}

              {errors.length > 0 && (
                <ul className="list-inside list-disc space-y-1 text-sm text-destructive">
                  {errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex min-h-[16rem] min-w-0 flex-col gap-3 rounded-md border border-border/60 bg-background/40 p-4 lg:min-h-0">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                <span>Preview</span>
                {draft.type === 'bracket' && <span>{placeholderCount} players</span>}
              </div>
              {draft.type === 'bracket' ? (
                <FitToBox>
                  <BracketView
                    stage={{ ...draft, participants: [], results: {} }}
                    placeholderCount={placeholderCount}
                    placeholderLabel={prev ? (n) => `${prev.name} #${n}` : undefined}
                  />
                </FitToBox>
              ) : (
                <div className="min-h-0 flex-1 overflow-hidden">
                  <LeaderboardStageView
                    stage={{ ...draft, participants: ['seed:1', 'seed:2', 'seed:3'], results: {} }}
                    playersById={PLACEHOLDER_PLAYERS_BY_ID}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="button" disabled={errors.length > 0 || busy} onClick={handleSave}>
              {busy ? 'Saving…' : stage ? 'Save' : 'Add stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default StageEditorDialog;
